import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gameAnalyticsApi, GARow, GAMeta } from '../../services/api/gameAnalytics';
import './GameTracker.css';

const WINDOWS: { win: number; label: string }[] = [
  { win: 1, label: '24h' },
  { win: 7, label: '7d' },
  { win: 30, label: '30d' },
];

const PAGE_SIZE = 50;

// Column -> sort key sent to the API. Only these keys are accepted server-side.
type Col = {
  key: string | null; // null = not sortable
  label: string;
  group: 'id' | 'twitch' | 'steam';
  render: (r: GARow) => React.ReactNode;
  cls?: string;
};

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1) + 'K';
  return String(Math.round(n));
}

function fmtPrice(cents: number | null): string {
  if (cents === null || cents === undefined) return '-';
  if (cents === 0) return 'Free';
  return '$' + (cents / 100).toFixed(2);
}

// Stable per-game accent derived from the slug (decorative only, no data implied).
function accentFor(slug: string): string {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 70% 62%)`;
}

function sumOf(rows: GARow[], k: 'watch_hrs' | 'avg_viewers' | 'ccu'): number | null {
  let t = 0;
  let any = false;
  for (const r of rows) {
    const v = r[k];
    if (v !== null && v !== undefined) {
      t += v;
      any = true;
    }
  }
  return any ? t : null;
}

function boxArt(url: string | null): string | null {
  if (!url) return null;
  return url.replace('{width}', '40').replace('{height}', '54');
}

export const GameTracker: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [win, setWin] = useState<number>(parseInt(searchParams.get('win') || '7', 10));
  const [genre, setGenre] = useState<string>(searchParams.get('genre') || '');
  const [steamOnly, setSteamOnly] = useState<boolean>(searchParams.get('steam') === '1');
  const [sort, setSort] = useState<string>(searchParams.get('sort') || 'rank');
  const [dir, setDir] = useState<'asc' | 'desc'>((searchParams.get('dir') as 'asc' | 'desc') || 'asc');
  const [page, setPage] = useState<number>(parseInt(searchParams.get('page') || '1', 10));

  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [debouncedQ, setDebouncedQ] = useState(searchTerm);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [rows, setRows] = useState<GARow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meta, setMeta] = useState<GAMeta | null>(null);

  useEffect(() => {
    document.title = 'Game Tracker | Practical Media';
  }, []);

  // Debounce search input. Only when the typed value actually differs from the applied one:
  // otherwise this ran on mount and reset a deep-linked ?page=N back to page 1.
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (searchTerm === debouncedQ) return;
    debounceTimer.current = setTimeout(() => {
      setDebouncedQ(searchTerm);
      setPage(1);
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchTerm, debouncedQ]);

  // Genre facet (per window)
  useEffect(() => {
    gameAnalyticsApi.meta(win).then(setMeta).catch(() => setMeta(null));
  }, [win]);

  // Main data fetch. `cancelled` ignores late responses so rapid win/sort/page clicks can't leave a
  // previous query's rows on screen.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    gameAnalyticsApi
      .list({
        win,
        genre: genre || undefined,
        q: debouncedQ || undefined,
        steam: steamOnly ? 1 : undefined,
        sort,
        dir,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setTotal(res.total);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [win, genre, steamOnly, debouncedQ, sort, dir, page]);

  // Sync state -> URL
  useEffect(() => {
    const p = new URLSearchParams();
    if (win !== 7) p.set('win', String(win));
    if (genre) p.set('genre', genre);
    if (steamOnly) p.set('steam', '1');
    if (debouncedQ) p.set('q', debouncedQ);
    if (sort !== 'rank') p.set('sort', sort);
    if (sort !== 'rank' && dir !== 'desc') p.set('dir', dir);
    if (sort === 'rank' && dir !== 'asc') p.set('dir', dir);
    if (page > 1) p.set('page', String(page));
    setSearchParams(p, { replace: true });
  }, [win, genre, steamOnly, debouncedQ, sort, dir, page, setSearchParams]);

  const onSort = (key: string | null) => {
    if (!key) return;
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir(key === 'rank' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const gameCell = (r: GARow) => {
    const art = boxArt(r.box_art_url);
    const steamUrl = r.steam_appid ? `https://store.steampowered.com/app/${r.steam_appid}/` : null;
    const twitchUrl = `https://www.twitch.tv/directory/game/${encodeURIComponent(r.name)}`;
    return (
      <div className="gt-game" style={{ ['--ga' as string]: accentFor(r.slug) }}>
        <i className="gt-bar" aria-hidden="true" />
        {art ? (
          <img src={art} alt="" className="gt-art" loading="lazy" width={40} height={54} />
        ) : (
          <span className="gt-art gt-art-empty" />
        )}
        <div className="gt-game-txt">
          <span className="gt-game-name">{r.name}</span>
          <span className="gt-game-links">
            <a href={twitchUrl} target="_blank" rel="noopener noreferrer" title="View on Twitch">
              twitch
            </a>
            {steamUrl && (
              <a href={steamUrl} target="_blank" rel="noopener noreferrer" title="View on Steam">
                steam
              </a>
            )}
          </span>
        </div>
      </div>
    );
  };

  const reviewsCell = (r: GARow) => {
    if (r.reviews === null || r.reviews === undefined) return '-';
    return (
      <span>
        {fmtNum(r.reviews)}
        {r.pct_positive !== null && r.pct_positive !== undefined && (
          <span className={`gt-pct ${r.pct_positive >= 70 ? 'gt-pct-good' : r.pct_positive >= 40 ? 'gt-pct-mid' : 'gt-pct-bad'}`}>
            {' '}
            {r.pct_positive}%
          </span>
        )}
      </span>
    );
  };

  const priceCell = (r: GARow) => {
    if (r.price === null || r.price === undefined) return '-';
    return (
      <span>
        {fmtPrice(r.price)}
        {r.discount ? <span className="gt-disc"> -{r.discount}%</span> : null}
      </span>
    );
  };

  const cols: Col[] = [
    { key: 'rank', label: '#', group: 'id', cls: 'gt-num gt-rank', render: (r) => r.rnk },
    { key: null, label: 'Game', group: 'id', cls: 'gt-game-col', render: gameCell },
    { key: null, label: 'Genre', group: 'id', cls: 'gt-genre', render: (r) => r.genre || '-' },
    // Steam group first, then Twitch. The grouped header row below mirrors this order.
    { key: 'ccu', label: 'Players', group: 'steam', cls: 'gt-num', render: (r) => fmtNum(r.ccu) },
    { key: 'reviews', label: 'Reviews', group: 'steam', cls: 'gt-num', render: reviewsCell },
    { key: 'sales', label: '~Sales', group: 'steam', cls: 'gt-num', render: (r) => fmtNum(r.est_sales) },
    { key: 'price', label: 'Price', group: 'steam', cls: 'gt-num', render: priceCell },
    { key: 'watch_hrs', label: 'Watch hrs', group: 'twitch', cls: 'gt-num', render: (r) => fmtNum(r.watch_hrs) },
    { key: 'avg_viewers', label: 'Avg viewers', group: 'twitch', cls: 'gt-num', render: (r) => fmtNum(r.avg_viewers) },
    { key: 'peak_viewers', label: 'Peak viewers', group: 'twitch', cls: 'gt-num', render: (r) => fmtNum(r.peak_viewers) },
    { key: 'avg_channels', label: 'Channels', group: 'twitch', cls: 'gt-num', render: (r) => fmtNum(r.avg_channels) },
  ];
  const nSteamCols = cols.filter((c) => c.group === 'steam').length;
  const nTwitchCols = cols.filter((c) => c.group === 'twitch').length;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const asOf = meta?.meta?.steam_latest;

  return (
    <div className="container gt-page">
      <header className="gt-head">
        <div className="label">Market data</div>
        <h1 className="page-title">Game Tracker</h1>
        {asOf && (
          <p className="gt-lede">
            <span className="gt-asof">Data as of {asOf}.</span>
          </p>
        )}
      </header>

      <div className="gt-kpis">
        <div className="gt-kpi">
          <span className="gt-kpi-l">Games matched</span>
          <b>{loading || error ? '-' : total.toLocaleString()}</b>
          <span className="gt-kpi-d">{WINDOWS.find((w) => w.win === win)?.label || ''} window</span>
        </div>
        <div className="gt-kpi">
          <span className="gt-kpi-l">Watch hrs (this page)</span>
          <b>{loading || error ? '-' : fmtNum(sumOf(rows, 'watch_hrs'))}</b>
          <span className="gt-kpi-d">Twitch</span>
        </div>
        <div className="gt-kpi">
          <span className="gt-kpi-l">Avg viewers (this page)</span>
          <b>{loading || error ? '-' : fmtNum(sumOf(rows, 'avg_viewers'))}</b>
          <span className="gt-kpi-d">Twitch</span>
        </div>
        <div className="gt-kpi">
          <span className="gt-kpi-l">Players (this page)</span>
          <b>{loading || error ? '-' : fmtNum(sumOf(rows, 'ccu'))}</b>
          <span className="gt-kpi-d">Steam concurrent</span>
        </div>
      </div>

      <div className="gt-controls">
        <div className="gt-wins" role="group" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button
              key={w.win}
              className={`gt-win ${win === w.win ? 'active' : ''}`}
              onClick={() => {
                setWin(w.win);
                setPage(1);
              }}
            >
              {w.label}
            </button>
          ))}
        </div>

        <select
          className="gt-select"
          value={genre}
          onChange={(e) => {
            setGenre(e.target.value);
            setPage(1);
          }}
          aria-label="Genre"
        >
          <option value="">All genres</option>
          {meta?.genres.map((g) => (
            <option key={g.genre} value={g.genre}>
              {g.genre} ({g.n})
            </option>
          ))}
          <option value="none">No genre</option>
        </select>

        <button
          type="button"
          className={`gt-toggle ${steamOnly ? 'active' : ''}`}
          aria-pressed={steamOnly}
          title="Only games with a Steam store page"
          onClick={() => {
            setSteamOnly((v) => !v);
            setPage(1);
          }}
        >
          Steam only
        </button>

        <input
          className="gt-search"
          type="text"
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="gt-table-wrap">
        <table className="gt-table">
          <thead>
            <tr className="gt-grp-row">
              <th colSpan={3} />
              <th colSpan={nSteamCols} className="gt-grp gt-grp-st">
                Steam
              </th>
              <th colSpan={nTwitchCols} className="gt-grp gt-grp-tw">
                Streaming (Twitch)
              </th>
            </tr>
            <tr>
              {cols.map((c, i) => {
                const active = c.key && c.key === sort;
                return (
                  <th
                    key={i}
                    className={`${c.cls || ''} ${c.key ? 'gt-sortable' : ''} ${active ? 'gt-active' : ''}`}
                    aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.key ? (
                      // A real button so sorting is keyboard-operable (Tab + Enter/Space).
                      <button type="button" className="gt-sort-btn" onClick={() => onSort(c.key)}>
                        {c.label}
                        {active && (
                          <svg className="gt-caret" viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
                            <path d={dir === 'asc' ? 'M5 2 9 8H1z' : 'M5 8 1 2h8z'} fill="currentColor" />
                          </svg>
                        )}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={cols.length} className="gt-msg">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={cols.length} className="gt-msg">
                  Couldn&rsquo;t load the data. Please try again in a moment.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={cols.length} className="gt-msg">
                  No games found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.slug}>
                  {cols.map((c, i) => (
                    <td key={i} className={c.cls || ''}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="gt-pagination">
        <button className="gt-pg" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
          prev
        </button>
        <span className="gt-page-info">
          page {page} of {totalPages} ({total.toLocaleString()} games)
        </span>
        <button className="gt-pg" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
          next
        </button>
      </div>

      <p className="gt-footnote">
        Watch hours, viewers and channels are windowed averages from Twitch. Players (concurrent), reviews, sales
        estimate and price come from Steam where a verified store page exists; a dash means no Steam data. Sales are a
        rough estimate (reviews x Boxleiter), not official figures.
      </p>
    </div>
  );
};
